import { type ConstraintCatalogEntry, type ConstraintLevel, constraintCatalog } from '@bp/shared-planung-utils';
import { Button, Card, Chip, EmptyState, Input, Select, SelectOptionType, Switch, Tooltip } from '@bp/ui-components';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePermissionChecker } from '../../../../../../hooks/usePermissionChecker';
import {
  useSetTimetableVersionConstraintSettingsMutation,
  useTimetableVersionConstraintSettingsQuery,
} from '../../../../../../types/planung-graphql-client-defs';
import { showSuccessSaveToast, showUserErrorToast } from '../../../../../../utils/toast';
import styles from './PlanTimetableConstraintSettings.module.scss';
import { type RuleText, useConstraintCatalogTexts } from './useConstraintCatalogTexts';

/*
 * Globaler Constraint-Katalog pro Stundenplan-Version („Regeln für die Generierung“). Rendert den geteilten
 * Solver-Katalog (@bp/shared-planung-utils) gruppiert nach Kategorie; pro Regel Schalter (enabled),
 * Stufe (nur wenn levelEditable) und Gewicht (nur omni-admins, editierbar nur bei weightSource OVERRIDE).
 * Speichert NUR Abweichungen vom Default als JSON-Blob auf TimetableVersion.constraintSettings
 * (leer ⇒ Solver-Defaults, score-exakt).
 */

type Setting = { enabled: boolean; level: ConstraintLevel; weight: number };
type StoredSetting = { id: string; enabled?: boolean; level?: ConstraintLevel; weight?: number };

const LEVELS: ConstraintLevel[] = ['HARD', 'MEDIUM', 'SOFT'];

const defaultFor = (entry: ConstraintCatalogEntry): Setting => ({
  enabled: true,
  level: entry.defaultLevel,
  weight: entry.defaultWeight,
});

const isDefault = (entry: ConstraintCatalogEntry, s: Setting): boolean =>
  s.enabled && s.level === entry.defaultLevel && s.weight === entry.defaultWeight;

// Tier-Familien/Dependencies (family != null) werden als EINE Zeile mit EINEM Gewicht dargestellt: die Stufe
// (hard/medium/soft) wird pro Bedingung/Karte über die Priorität gewählt, nicht hier — das eine Gewicht gilt
// für die wirksame Stufe. Singletons (family == null) bleiben einzeln.
const TIER_LABEL = /\s*\((hart|mittel|weich)\)\s*$/i;
const familyBaseLabel = (rep: ConstraintCatalogEntry): string => rep.label.replace(TIER_LABEL, '').trim();
const representative = (entries: ConstraintCatalogEntry[]): ConstraintCatalogEntry =>
  entries.find((e) => e.id.endsWith('-medium')) ?? entries[0];
const splitFamilies = (entries: ConstraintCatalogEntry[]) => {
  const singles: ConstraintCatalogEntry[] = [];
  const families = new Map<string, ConstraintCatalogEntry[]>();
  for (const entry of entries) {
    if (entry.family == null) {
      singles.push(entry);
      continue;
    }
    const list = families.get(entry.family) ?? [];
    list.push(entry);
    families.set(entry.family, list);
  }
  return { singles, families: [...families.values()] };
};

// leeres Feld zulassen (→ 0); negatives Gewicht auf 0 klemmen (kein Reward via Negativwert).
const parseWeight = (raw: string): number => (raw === '' ? 0 : Math.max(0, Number(raw) || 0));

type RuleRowProps = {
  id: string;
  text: RuleText;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  level: ReactNode;
  weight: ReactNode;
};

export const PlanTimetableConstraintSettings = () => {
  const { t } = useTranslation();
  const { versionUuid } = useParams();
  const texts = useConstraintCatalogTexts();
  // Einzelgewichte (numerische Constraint-Gewichte) sind ein technisches Power-User-Feature und nur für
  // omni-admins sichtbar; reguläre Nutzer sehen weiterhin Schalter (enabled) und Stufe.
  const permissions = usePermissionChecker();
  const isOmniAdmin = permissions?.isOmniAdmin() ?? false;

  // Wo die Stufe einer Familie gepflegt wird: bei der Klasse, dem Fach, der Lehrkraft oder der Abhängigkeit.
  const levelWhereText = (category: ConstraintCatalogEntry['category']): string => {
    switch (category) {
      case 'CLASS':
      case 'LUNCH':
        return t('constraintCatalog.levelWhere.class');
      case 'SUBJECT':
      case 'DISTRIBUTION':
        return t('constraintCatalog.levelWhere.subject');
      case 'TEACHER':
        return t('constraintCatalog.levelWhere.teacher');
      case 'DEPENDENCY':
        return t('constraintCatalog.levelWhere.dependency');
      default:
        return t('constraintCatalog.levelPerCondition');
    }
  };
  // Stundenplan View sieht die Einstellungen, ändert sie aber nicht.
  const canEdit = permissions?.canWriteTimetable() ?? false;

  const [{ data, fetching, error }, refetch] = useTimetableVersionConstraintSettingsQuery({
    variables: { versionUuid: versionUuid ?? '' },
    pause: !versionUuid,
    requestPolicy: 'cache-and-network',
  });
  const [, updateSettings] = useSetTimetableVersionConstraintSettingsMutation();

  const stored: Record<string, StoredSetting> = useMemo(() => {
    const raw = data?.timetableVersionConstraintSettings;
    if (!raw) return {};
    try {
      const arr = JSON.parse(raw) as StoredSetting[];
      return Object.fromEntries(arr.map((s) => [s.id, s]));
    } catch {
      return {};
    }
  }, [data]);

  const initial: Record<string, Setting> = useMemo(() => {
    const result: Record<string, Setting> = {};
    for (const entry of constraintCatalog.constraints) {
      const base = defaultFor(entry);
      const s = stored[entry.id];
      result[entry.id] = s
        ? { enabled: s.enabled ?? base.enabled, level: s.level ?? base.level, weight: s.weight ?? base.weight }
        : base;
    }
    return result;
  }, [stored]);

  const [settings, setSettings] = useState<Record<string, Setting>>(initial);
  // Seed from the loaded blob: on version change always; otherwise only while the user has NOT edited yet —
  // so a background cache-and-network response never discards in-flight edits (P0: silent data loss).
  const dirtyRef = useRef(false);
  const seededVersionRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (seededVersionRef.current !== versionUuid || !dirtyRef.current) {
      setSettings(initial);
      seededVersionRef.current = versionUuid;
      dirtyRef.current = false;
    }
  }, [initial, versionUuid]);

  const byCategory = useMemo(() => {
    const groups: Record<string, ConstraintCatalogEntry[]> = {};
    for (const entry of constraintCatalog.constraints) {
      (groups[entry.category] ??= []).push(entry);
    }
    return groups;
  }, []);

  const levelOptions: SelectOptionType[] = useMemo(
    () => LEVELS.map((l) => ({ label: texts.levels[l], value: l })),
    [texts],
  );

  const update = (id: string, patch: Partial<Setting>) => {
    dirtyRef.current = true;
    setSettings((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  // Familien-Patch: dasselbe enabled/Gewicht auf ALLE Tier-Varianten der Familie anwenden.
  const updateMany = (ids: string[], patch: Partial<Setting>) => {
    dirtyRef.current = true;
    setSettings((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = { ...next[id], ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    if (!versionUuid) return;
    // Nur editierbare Felder serialisieren — sonst würde level/weight für gesperrte bzw. FACT/COMPUTED-
    // Constraints auf den Katalog-Default gepinnt (Backend-Wire akzeptiert partielle Einträge {id, enabled, …}).
    const blob: StoredSetting[] = constraintCatalog.constraints
      .filter((entry) => !isDefault(entry, settings[entry.id]))
      .map((entry) => {
        const s = settings[entry.id];
        const out: StoredSetting = { id: entry.id, enabled: s.enabled };
        if (entry.levelEditable) out.level = s.level;
        if (entry.weightSource === 'OVERRIDE') out.weight = s.weight;
        return out;
      });
    const { error: saveError } = await updateSettings({ versionUuid, data: JSON.stringify(blob) });
    if (saveError) {
      showUserErrorToast({ error: saveError });
      return;
    }
    dirtyRef.current = false;
    showSuccessSaveToast();
    refetch({ requestPolicy: 'network-only' });
  };

  const handleReset = () => {
    dirtyRef.current = true;
    const reset: Record<string, Setting> = {};
    for (const entry of constraintCatalog.constraints) reset[entry.id] = defaultFor(entry);
    setSettings(reset);
  };

  if (!versionUuid) {
    return <EmptyState title={t('constraintCatalog.noVersion')} padding='xl' />;
  }

  // Query-Fehler sichtbar machen — sonst stünde data=undefined und die Seite zeigte alle Defaults,
  // was echte gespeicherte Overrides verdecken (und beim Speichern überschreiben) würde.
  if (error) {
    return <EmptyState title={t('constraintCatalog.loadError')} padding='xl' />;
  }

  const levelChip = (level: ConstraintLevel, hint: string) => (
    <span title={hint}>
      <Chip value={texts.levels[level]} title={hint} maxWidth='none' className={styles[`level-${level}`]} />
    </span>
  );

  const weightInput = (name: string, value: number, editable: boolean, onChange: (weight: number) => void) => (
    <div className={styles.weight} title={editable ? undefined : t('constraintCatalog.weightFactHint')}>
      <Input
        type='number'
        name={name}
        dense
        min={0}
        value={String(value)}
        disabled={!editable}
        onChange={(e) => onChange(parseWeight(e.target.value))}
      />
    </div>
  );

  // Render-Funktion statt Komponente: eine innerhalb definierte Komponente würde bei jedem Render neu montiert
  // (Fokusverlust im Gewichtsfeld).
  const ruleRow = ({ id, text, enabled, onToggle, level, weight }: RuleRowProps) => (
    <div key={id} className={styles.row} data-disabled={!enabled || undefined}>
      <div className={styles.toggle}>
        <Switch
          name={`${id}.enabled`}
          size='small'
          checked={enabled}
          disabled={!canEdit}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </div>
      <div className={styles.text}>
        <div className={styles.name}>
          <span>{text.name}</span>
        </div>
        {text.desc && <div className={styles.desc}>{text.desc}</div>}
      </div>
      <div className={styles.level}>{level}</div>
      {isOmniAdmin && <div className={styles.weightCell}>{weight}</div>}
    </div>
  );

  const ruleText = (key: string, fallback: string): RuleText => texts.rules[key] ?? { name: fallback, desc: '' };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <h2 className={styles.toolbarTitle}>{t('constraintCatalog.title')}</h2>
        {canEdit && (
          <div className={styles.actions}>
            <Button hierarchy='tertiary' onClick={handleReset}>
              {t('constraintCatalog.reset')}
            </Button>
            <Button hierarchy='primary' onClick={handleSave} disabled={fetching}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </div>

      <Card className={styles.card}>
        <div className={styles.intro}>
          <p>{t('constraintCatalog.intro.text')}</p>
          <p className={styles.introHeading}>{t('constraintCatalog.intro.levelsHeading')}</p>
          <ul className={styles.levels}>
            {LEVELS.map((level) => (
              <li key={level}>
                <Chip value={texts.levels[level]} maxWidth='none' className={styles[`level-${level}`]} />
                <span>
                  {level === 'HARD'
                    ? t('constraintCatalog.intro.HARD')
                    : level === 'MEDIUM'
                      ? t('constraintCatalog.intro.MEDIUM')
                      : t('constraintCatalog.intro.SOFT')}
                </span>
              </li>
            ))}
          </ul>
          <p>{t('constraintCatalog.intro.off')}</p>
          <p className={styles.introHeading}>{t('constraintCatalog.intro.tipsHeading')}</p>
          <ul className={styles.tips}>
            <li>{t('constraintCatalog.intro.tipVariants')}</li>
            <li>{t('constraintCatalog.intro.tipDraft')}</li>
            <li>{t('constraintCatalog.intro.tipNotAll')}</li>
            <li>{t('constraintCatalog.intro.tipOff')}</li>
          </ul>
          {isOmniAdmin && (
            <p>
              {t('constraintCatalog.intro.weight')} {t('constraintCatalog.intro.weightRatio')}
            </p>
          )}
        </div>
      </Card>

      {Object.entries(byCategory).map(([category, entries]) => {
        const { singles, families } = splitFamilies(entries);
        const categoryText = texts.categories[category] ?? { title: category, desc: '' };
        return (
          <Card key={category} className={styles.card} header={{ headline: categoryText.title }}>
            {categoryText.desc && <p className={styles.categoryDesc}>{categoryText.desc}</p>}
            <div className={styles.table}>
              <div className={`${styles.row} ${styles.head}`} aria-hidden>
                <div className={styles.toggle}>{t('constraintCatalog.column.active')}</div>
                <div className={styles.text}>{t('constraintCatalog.column.rule')}</div>
                <div className={styles.level}>{t('constraintCatalog.level.label')}</div>
                {isOmniAdmin && <div className={styles.weightCell}>{t('constraintCatalog.weight')}</div>}
              </div>
              {singles.map((entry) => {
                const s = settings[entry.id] ?? defaultFor(entry);
                return ruleRow({
                  id: entry.id,
                  text: ruleText(entry.id, entry.label),
                  enabled: s.enabled,
                  onToggle: (enabled) => update(entry.id, { enabled }),
                  level: entry.levelEditable ? (
                    <div className={styles.select}>
                      <Select
                        name={`${entry.id}.level`}
                        options={levelOptions}
                        value={levelOptions.find((o) => o.value === s.level) ?? null}
                        disabled={!canEdit}
                        dense
                        onChange={(opt) => {
                          const value = (opt as SelectOptionType | null)?.value as ConstraintLevel | undefined;
                          if (value) update(entry.id, { level: value });
                        }}
                        menuPosition='fixed'
                      />
                    </div>
                  ) : (
                    levelChip(entry.defaultLevel, t('constraintCatalog.levelLocked'))
                  ),
                  weight: weightInput(`${entry.id}.weight`, s.weight, entry.weightSource === 'OVERRIDE', (weight) =>
                    update(entry.id, { weight }),
                  ),
                });
              })}
              {families.map((fam) => {
                // Eine Zeile je Familie: ein enabled + ein Gewicht für alle Tier-Varianten; Stufe je Bedingung/Karte.
                const rep = representative(fam);
                const s = settings[rep.id] ?? defaultFor(rep);
                const ids = fam.map((e) => e.id);
                const key = rep.family ?? rep.id;
                return ruleRow({
                  id: key,
                  text: ruleText(key, familyBaseLabel(rep)),
                  enabled: s.enabled,
                  onToggle: (enabled) => updateMany(ids, { enabled }),
                  level: (
                    <Tooltip content={levelWhereText(rep.category)}>
                      <span className={styles.perCondition}>
                        {rep.category === 'DEPENDENCY'
                          ? t('constraintCatalog.levelPerDependencyShort')
                          : t('constraintCatalog.levelPerConditionShort')}
                      </span>
                    </Tooltip>
                  ),
                  weight: weightInput(`${key}.weight`, s.weight, rep.weightSource === 'OVERRIDE', (weight) =>
                    updateMany(ids, { weight }),
                  ),
                });
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
};
